import { useState } from "react";
import {
  ArrowLeft,
  Camera,
  Lightbulb,
  Eye,
  ZoomIn,
  AlertCircle,
  ScanBarcode,
} from "lucide-react";
import { useNavigate } from "react-router-dom";
import { Button } from "@/projects/Admin_Bite_Balance/bite-balance/src/components/ui/button";
import { Card } from "@/projects/Admin_Bite_Balance/bite-balance/src/components/ui/card";

const CameraScreen = () => {
  const navigate = useNavigate();
  const [showTips, setShowTips] = useState(true);

  const tips = [
    {
      icon: Lightbulb,
      title: "Good Lighting",
      description:
        "Use natural light or bright indoor lighting. Avoid harsh shadows.",
    },
    {
      icon: Eye,
      title: "Top-Down Angle",
      description: "Hold your phone directly above the food at a 45-90° angle.",
    },
    {
      icon: ZoomIn,
      title: "Fill the Frame",
      description: "Get close enough so the food fills most of the screen.",
    },
    {
      icon: AlertCircle,
      title: "Clear Background",
      description:
        "Use a clean plate or simple background for better recognition.",
    },
  ];

  return (
    <div className="min-h-screen bg-background pb-24">
      {/* Header */}
      <div className="bg-gradient-to-b from-primary/10 to-background px-4 pb-6 pt-8">
        <div className="mb-4 flex items-center justify-between">
          <Button
            variant="ghost"
            size="icon"
            onClick={() => navigate(-1)}
            className="rounded-full"
          >
            <ArrowLeft className="h-5 w-5" />
          </Button>
          <h1 className="text-2xl font-bold">Log Meal</h1>
          <div className="w-10" />
        </div>
      </div>

      <div className="space-y-6 px-4">
        {/* Tips Section */}
        {showTips && (
          <Card className="border-primary/20 bg-primary/5 p-4">
            <div className="mb-3 flex items-start justify-between">
              <h2 className="text-lg font-bold text-foreground">
                Photography Tips
              </h2>
              <Button
                variant="ghost"
                size="sm"
                onClick={() => setShowTips(false)}
                className="h-6 px-2 text-xs"
              >
                Hide
              </Button>
            </div>
            <div className="grid grid-cols-1 gap-3">
              {tips.map((tip, index) => {
                const Icon = tip.icon;
                return (
                  <div key={index} className="flex items-start gap-3">
                    <div className="flex h-8 w-8 flex-shrink-0 items-center justify-center rounded-full bg-primary/10">
                      <Icon className="h-4 w-4 text-primary" />
                    </div>
                    <div className="flex-1">
                      <p className="text-base font-bold text-foreground">
                        {tip.title}
                      </p>
                      <p className="text-sm text-muted-foreground">
                        {tip.description}
                      </p>
                    </div>
                  </div>
                );
              })}
            </div>
          </Card>
        )}

        {!showTips && (
          <Button
            variant="outline"
            size="sm"
            onClick={() => setShowTips(true)}
            className="w-full"
          >
            Show Tips
          </Button>
        )}

        {/* Camera Preview Area */}
        <div className="relative aspect-[3/4] overflow-hidden rounded-2xl border-2 border-border bg-muted">
          <div className="absolute inset-0 flex items-center justify-center">
            <div className="space-y-4 text-center">
              <div className="mx-auto flex h-20 w-20 items-center justify-center rounded-full bg-primary/10">
                <Camera className="h-10 w-10 text-primary" />
              </div>
              <div>
                <p className="text-lg font-bold text-foreground">
                  Ready to capture
                </p>
                <p className="text-base text-muted-foreground">
                  Position your food in the frame
                </p>
              </div>
            </div>
          </div>

          {/* Grid Overlay */}
          <div className="pointer-events-none absolute inset-0">
            <div className="grid h-full w-full grid-cols-3 grid-rows-3">
              {[...Array(9)].map((_, i) => (
                <div key={i} className="border border-white/20" />
              ))}
            </div>
          </div>
        </div>

        {/* Action Buttons */}
        <div className="flex items-center justify-center gap-6">
          <Button
            variant="outline"
            size="lg"
            className="h-16 w-16 flex-col gap-0.5 rounded-full"
            onClick={() => navigate("/barcode")}
          >
            <ScanBarcode className="h-6 w-6" />
          </Button>
          <Button
            size="lg"
            className="h-20 w-20 rounded-full"
            onClick={() => navigate("/meals/prediction")}
          >
            <div className="h-16 w-16 rounded-full border-4 border-primary-foreground" />
          </Button>
          <div className="w-16" />
        </div>

        {/* Instructions */}
        <Card className="p-4">
          <h3 className="mb-2 text-lg font-bold text-foreground">
            How it works
          </h3>
          <ol className="space-y-2 text-base text-muted-foreground">
            <li className="flex gap-2">
              <span className="font-semibold text-primary">1.</span>
              <span>Position your food in the camera frame</span>
            </li>
            <li className="flex gap-2">
              <span className="font-semibold text-primary">2.</span>
              <span>Tap the capture button to take a photo</span>
            </li>
            <li className="flex gap-2">
              <span className="font-semibold text-primary">3.</span>
              <span>Our AI will identify the food and nutritional values</span>
            </li>
            <li className="flex gap-2">
              <span className="font-semibold text-primary">4.</span>
              <span>Review and edit the details before saving</span>
            </li>
          </ol>
        </Card>
      </div>
    </div>
  );
};

export default CameraScreen;
